import React, { useEffect, useState } from "react";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import InfoCard from "../../../../Components/Common/InfoCard/InfoCard";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  companySiteLocationListApi,
  companySiteLocationCreateApi,
  companySiteLocationUpdateApi,
  companySiteLocationDeleteApi,
} from "../../../../helpers/fakebackend_helper";

const EMPTY = {
  Name: "",
  SiteReference: "",
  AddressLine1: "",
  AddressLine2: "",
  AddressCity: "",
  AddressCounty: "",
  AddressPostCode: "",
  AddressCountry: "",
  ContactTelephoneNumber: "",
  ContactEmailAddress: "",
  Notes: "",
  FlagDefault: 0,
  FlagActive: 1,
  Order: "",
};

/**
 * Task 24472 - the company's own premises.
 *
 * Deliberately NOT CompanyStorageLocation: that table sounds right but is a
 * JobStatus clone with no address fields at all, so it cannot say where
 * anything is.
 */
export default function CompanySiteLocations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await companySiteLocationListApi();
      setRows(res?.companysitelocation_list || []);
    } catch (e) {
      toast.error("Could not load site locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const save = async () => {
    if (!form.Name.trim()) {
      toast.error("Give the site a name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        Order: form.Order === "" ? null : Number(form.Order),
        FlagDefault: form.FlagDefault ? 1 : 0,
        FlagActive: form.FlagActive ? 1 : 0,
      };
      const res = editingId
        ? await companySiteLocationUpdateApi([{ Id: editingId, ...payload }])
        : await companySiteLocationCreateApi([payload]);
      if (res?.status === 200) {
        toast.success(res.message || "Saved");
        reset();
        load();
      } else {
        toast.error(res?.message || "Could not save that site");
      }
    } catch (e) {
      toast.error("Could not save that site");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      const res = await companySiteLocationDeleteApi([deleteId]);
      if (res?.status === 200) {
        toast.success(res.message || "Deleted");
        load();
      } else {
        toast.error(res?.message || "Could not delete that site");
      }
    } catch (e) {
      toast.error("Could not delete that site");
    } finally {
      setDeleteId(null);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.Id);
    setForm({
      Name: r.Name || "",
      SiteReference: r.SiteReference || "",
      AddressLine1: r.AddressLine1 || "",
      AddressLine2: r.AddressLine2 || "",
      AddressCity: r.AddressCity || "",
      AddressCounty: r.AddressCounty || "",
      AddressPostCode: r.AddressPostCode || "",
      AddressCountry: r.AddressCountry || "",
      ContactTelephoneNumber: r.ContactTelephoneNumber || "",
      ContactEmailAddress: r.ContactEmailAddress || "",
      Notes: r.Notes || "",
      FlagDefault: String(r.FlagDefault) === "1" ? 1 : 0,
      FlagActive: String(r.FlagActive) === "0" ? 0 : 1,
      Order: r.Order ?? "",
    });
  };

  const f = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <SettingsPanel
      title="Site Locations"
      description="Your own premises — the depot, yard or unit staff start and finish from."
    >
      <DeleteModal
        show={!!deleteId}
        onCloseClick={() => setDeleteId(null)}
        onDeleteClick={remove}
      />

      <InfoCard
        title={editingId ? "Edit site" : "Add a site"}
        icon="ri-map-pin-line"
        accent="indigo"
      >
        <Row className="g-2">
          <Col lg={4}>
            <Label className="mb-1">Name</Label>
            <Input value={form.Name} onChange={f("Name")} placeholder="e.g. Gloucester Depot" />
          </Col>
          <Col lg={2}>
            <Label className="mb-1">Reference</Label>
            <Input value={form.SiteReference} onChange={f("SiteReference")} />
          </Col>
          <Col lg={4}>
            <Label className="mb-1">Address line 1</Label>
            <Input value={form.AddressLine1} onChange={f("AddressLine1")} />
          </Col>
          <Col lg={2}>
            <Label className="mb-1">Postcode</Label>
            <Input value={form.AddressPostCode} onChange={f("AddressPostCode")} />
          </Col>
          <Col lg={4}>
            <Label className="mb-1">Address line 2</Label>
            <Input value={form.AddressLine2} onChange={f("AddressLine2")} />
          </Col>
          <Col lg={3}>
            <Label className="mb-1">City</Label>
            <Input value={form.AddressCity} onChange={f("AddressCity")} />
          </Col>
          <Col lg={3}>
            <Label className="mb-1">County</Label>
            <Input value={form.AddressCounty} onChange={f("AddressCounty")} />
          </Col>
          <Col lg={2}>
            <Label className="mb-1">Country</Label>
            <Input value={form.AddressCountry} onChange={f("AddressCountry")} />
          </Col>
          <Col lg={3}>
            <Label className="mb-1">Telephone</Label>
            <Input value={form.ContactTelephoneNumber} onChange={f("ContactTelephoneNumber")} />
          </Col>
          <Col lg={4}>
            <Label className="mb-1">Email</Label>
            <Input type="email" value={form.ContactEmailAddress} onChange={f("ContactEmailAddress")} />
          </Col>
          <Col lg={3}>
            <Label className="mb-1">Notes</Label>
            <Input value={form.Notes} onChange={f("Notes")} />
          </Col>
          <Col lg={2}>
            <Label className="mb-1">Order</Label>
            <Input type="number" value={form.Order} onChange={f("Order")} />
          </Col>
          <Col lg={12}>
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="siteDefault"
                checked={!!form.FlagDefault}
                onChange={(e) => setForm({ ...form, FlagDefault: e.target.checked ? 1 : 0 })}
              />
              <Label className="form-check-label" for="siteDefault">
                Default — offered first when picking a shift start or end location
              </Label>
            </div>
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="siteActive"
                checked={!!form.FlagActive}
                onChange={(e) => setForm({ ...form, FlagActive: e.target.checked ? 1 : 0 })}
              />
              <Label className="form-check-label" for="siteActive">
                Active
              </Label>
            </div>
          </Col>
          <Col lg={12}>
            <div className="d-flex gap-2">
              <Button color="success" disabled={saving} onClick={save}>
                <i className="ri-save-line align-bottom me-1" />
                {editingId ? "Save changes" : "Add site"}
              </Button>
              {editingId && (
                <Button color="light" onClick={reset}>
                  Cancel
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </InfoCard>

      <InfoCard title="Sites" icon="ri-building-2-line" accent="indigo">
        {loading ? (
          <LoadingSpinner />
        ) : isEmpty(rows) ? (
          <p className="text-muted mb-0">
            No sites yet. Add your depot or yard and it becomes selectable as a
            shift start and end location.
          </p>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-wrap mb-0">
              <thead className="text-muted">
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.Id}>
                    <td>
                      {r.Name}
                      {String(r.FlagDefault) === "1" && (
                        <span className="badge bg-primary-subtle text-primary ms-2">
                          Default
                        </span>
                      )}
                      {r.SiteReference && (
                        <div className="text-muted small">{r.SiteReference}</div>
                      )}
                    </td>
                    <td className="text-muted">{r.AddressOneLine || "—"}</td>
                    <td>
                      {String(r.FlagActive) === "0" ? (
                        <span className="badge bg-secondary-subtle text-secondary">Inactive</span>
                      ) : (
                        <span className="badge bg-success-subtle text-success">Active</span>
                      )}
                    </td>
                    <td className="text-end text-nowrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-1"
                        title="Edit"
                        onClick={() => startEdit(r)}
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        title="Delete"
                        onClick={() => setDeleteId(r.Id)}
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </InfoCard>
    </SettingsPanel>
  );
}
